<script setup lang="ts">
/**
 * Below lg the sidebar is a drawer, so the four destinations a phone needs sit here.
 * Same tone dots as the sidebar: only work in flight is announced.
 */
import Icon from '~/components/ui/Icon.vue'
import StatusDot from '~/components/ui/StatusDot.vue'
import type { Tone } from '~/utils/state'

type Badge = { count?: number | null; tone?: Tone }
defineEmits<{ more: [] }>()
const props = defineProps<{
  nav: { datasets?: Badge; models?: Badge } | null | undefined
  active: (to: string) => boolean
}>()

/** Same rule as the sidebar: announce work in flight, stay silent on a normal state. */
const badge = (key: 'datasets' | 'models' | null): Tone | null => {
  if (!key) return null
  const tone = props.nav?.[key]?.tone
  return tone && tone !== 'normal' ? tone : null
}

const items = [
  { to: '/overview', label: 'Overview', icon: 'overview' as const, key: null },
  { to: '/datasets', label: 'Datasets', icon: 'datasets' as const, key: 'datasets' as const },
  { to: '/models', label: 'Models', icon: 'models' as const, key: 'models' as const },
  { to: '/playground', label: 'Playground', icon: 'playground' as const, key: null },
]
</script>

<template>
  <nav class="fixed inset-x-0 bottom-0 z-30 grid h-14 grid-cols-5 border-t border-sidebar-border bg-sidebar lg:hidden">
    <NuxtLink
v-for="i in items" :key="i.to" :to="i.to"
      class="relative flex flex-col items-center justify-center gap-1 text-[length:var(--type-fine)] no-underline hover:no-underline"
      :class="active(i.to) ? 'font-semibold text-primary' : 'text-muted-foreground'">
      <Icon :name="i.icon" :size="18" />
      {{ i.label }}
      <span v-if="badge(i.key)" class="absolute right-[var(--measure-22px)] top-[var(--measure-9px)]">
        <StatusDot status="" :tone="badge(i.key) ?? undefined" />
      </span>
    </NuxtLink>
    <button type="button" class="flex flex-col items-center justify-center gap-1 text-[length:var(--type-fine)] text-muted-foreground" @click="$emit('more')">
      <Icon name="kebab" :size="18" />More
    </button>
  </nav>
</template>
